/* eslint-disable max-len */
/**
 * ImageExperienceVideo Class
 * This class is used internally to the ImageExperience pacakge
 * Main purpose is to have a consistant object across different components
 *
 * @class ImageExperienceVideo
 */
class ImageExperienceVideo {
  /**
   * Creates an instance of ImageExperienceVideo.
   *
   * @param {Object} videoObj - video object
   * @param {id} videoObj.id - id of the video
   * @param {String} videoObj.idString - id of the video
   * @param {Number} videoObj.width - Width of the video
   * @param {Number} videoObj.heigth - Height of the video
   * @param {string} videoObj.type - Type of the video
   */
  constructor(videoObj) {
    this.id = videoObj.id;
    this.idString = videoObj.idString;
    this.thumbnailUrl = videoObj.thumbnailUrl;
    this.type = videoObj.type;
    this.width = videoObj.width;
    this.height = videoObj.height;
    this.text = videoObj.text;
    this.src = videoObj.src;
    this.status = videoObj.status;
    this.failureCode = videoObj.failureCode;
    this.duration = videoObj.duration;
    this.altText = videoObj.altText;
    this.srcUrl = videoObj.srcUrl;
    this.quality = videoObj.quality;
    this.sourceVideoBitRate = videoObj.sourceVideoBitRate;
    this.sourceType = videoObj.SourceType;
    this.sourceUrl = videoObj.SourceUrl;
    this.description = videoObj.Description;
    this.metadata = videoObj.metadata;
    this.template = videoObj.template;
    this.recommendationId = videoObj.recommendationId;
    this.savedMaterial = videoObj.savedMaterial;
  }

  /**
   * Get source of the video
   *
   * @readonly
   * @returns {Number} - id of the saved video
   */
  get Id() {
    return this.id;
  }

  /**
   * Get source of the video
   *
   * @readonly
   * @returns {string} - id of the saved video
   */
  get IdString() {
    return this.idString;
  }

  /**
   * Get thumbnail of the video
   *
   * @readonly
   * @returns {string} - string thumbnail of the video either base64 or url
   */
  get ThumbnailUrl() {
    return this.thumbnailUrl;
  }

  /**
   * Get thumbnail of the video for ad list
   *
   * @readonly
   * @returns {string} - string thumbnail of the video either base64 or url
   */
  get ThumbnailForAdList() {
    return this.thumbnailUrl;
  }

  /**
   * Get width of the video
   *
   * @readonly
   * @returns {Number} - Width of the video
   */
  get Width() {
    return this.width;
  }

  /**
   * Get height of the video
   *
   * @readonly
   * @returns {Number} - Height of the video
   */
  get Height() {
    return this.height;
  }

  /**
   * Get type of the video
   *
   * @returns {string} - Type of the video
   */
  get Type() {
    return this.type;
  }

  /**
   * Get text of the video
   *
   * @readonly
   * @returns {string} - Text of the video
   */
  get Text() {
    return this.text;
  }

  /**
   * Get duration of the video
   *
   * @readonly
   * @returns {string} - Text of the video
   */
  get Duration() {
    return this.duration;
  }

  /**
   * Get src url of the video
   *
   * @readonly
   * @returns {string} - src url of video
   */
  get Src() {
    return this.src;
  }

  /**
   * Get status of the video
   *
   * @readonly
   * @returns {string} - status of video
   */
  get Status() {
    return this.status;
  }

  /**
   * Get status of the video
   *
   * @readonly
   * @returns {string} - status of video
   */
  get FailureCode() {
    return this.failureCode;
  }

  /**
   * Get the clone of this object
   *
   * @returns {ImageExperienceVideo} - cloned ImageExperienceVideo
   */
  clone() {
    return new ImageExperienceVideo(this);
  }

  /**
   * Get the object formatted in JSON to save
   *
   * @returns {Object} - Object formatted to be saved on MT
   */
  formatToSave() {
    return {
      id: this.Id,
      idString: this.IdString,
      subType: this.Type,
    };
  }
}

const getVideoSrc = (DownloadUrl) => {
  if (!DownloadUrl) return null;
  const downloadUrlMapping = JSON.parse(DownloadUrl);
  const resolutions = Object.keys(downloadUrlMapping).map(h => parseInt(h, 10));
  const highestResolution = Math.max(...resolutions);
  return downloadUrlMapping[highestResolution];
};

const getCommonVideoFields = (videoObject) => {
  let template;
  let { recommendationId } = videoObject;

  if (videoObject.Metadata && !videoObject.Metadata.includes('VideoMetadataBlobName')) {
    const metadata= JSON.parse(videoObject.Metadata);
    if (metadata) {
      ({ template } = metadata);
      ({ recommendationId } = metadata);
    }
  }

  return {
    src: getVideoSrc(videoObject.DownloadUrl),
    width: videoObject.Width,
    height: videoObject.Height,
    type: videoObject.SubType,
    text: videoObject.Description,
    status: videoObject.Status,
    thumbnailUrl: videoObject.ThumbnailUrl,
    failureCode: videoObject.FailureCode,
    duration: videoObject.DurationInMilliSeconds || videoObject.DurationInMS,
    srcUrl: videoObject.SourceUrl,
    quality: videoObject.Quality,
    sourceVideoBitRate: videoObject.SourceVideoBitRate,
    metadata: videoObject.Metadata,
    recommendationId,
    template,
    SourceType: videoObject.SourceType,
    SourceUrl: videoObject.SourceUrl,
    Description: videoObject.Description,
  };
};

/**
 * Returns a {ImageExperienceVideo} object
 *
 * @param {object} videoMT - object returned from MT for the video whose Id is string and no IdString field
 * @returns {ImageExperienceVideo} - ImageExperienceVideo object
 */
const newVideoFromMTObject = (videoMT) => {
  const imageObj = new ImageExperienceVideo({
    id: parseInt(videoMT.Id, 10),
    idString: String(videoMT.Id),
    ...getCommonVideoFields(videoMT),
  });

  return imageObj;
};

/**
 * Returns a {ImageExperienceVideo} object
 *
 * @param {object} videoGrid - object returned from Grid for the video whose Id is integer and IdString is string
 * @returns {ImageExperienceVideo} - ImageExperienceVideo object
 */
const newVideoFromGridObject = (videoGrid) => {
  const imageObj = new ImageExperienceVideo({
    id: videoGrid.Id,
    idString: videoGrid.IdString,
    ...getCommonVideoFields(videoGrid),
  });

  return imageObj;
};

export {
  ImageExperienceVideo,
  newVideoFromMTObject,
  newVideoFromGridObject,
};
